import {useQuery} from '@tanstack/react-query';
import {apiClient} from '@common/http/query-client';
import {prettyBytes} from '@common/uploads/utils/pretty-bytes';
import {isEmail} from '@common/utils/string/is-email';
import {
  fetchStorageSummary,
  type FetchStorageSummaryResponse,
} from '../drive/layout/sidebar/storage-summary/storage-summary';
import {fetchGuestSendQuota} from './guest-send-api';

/** Matches App\GuestSend\GuestSend::MAX_BYTES */
const GUEST_DEFAULT_MAX_BYTES = 25 * 1024 * 1024;

function compactBytes(bytes: number | null | undefined): string {
  if (bytes == null) return '';
  return prettyBytes(bytes).replace(' ', '');
}

export function useSendTransferLimit(
  isLoggedIn: boolean,
  guestEmail: string,
) {
  const normalizedGuestEmail = guestEmail.trim().toLowerCase();

  const userQuery = useQuery({
    queryKey: ['send-transfer-limit', 'user'],
    queryFn: fetchStorageSummary,
    enabled: isLoggedIn,
    staleTime: 30_000,
  });

  const guestQuery = useQuery({
    queryKey: ['send-transfer-limit', 'guest', normalizedGuestEmail],
    queryFn: () => fetchGuestSendQuota(normalizedGuestEmail),
    enabled: !isLoggedIn && isEmail(normalizedGuestEmail),
    staleTime: 30_000,
  });

  if (isLoggedIn) {
    const data = userQuery.data as FetchStorageSummaryResponse | undefined;
    const available = data?.transfer_available;

    if (userQuery.isLoading) {
      return {label: '…', isLoading: true, isUnlimited: false};
    }

    if (available === null || available === undefined) {
      return {label: 'Unlimited', isLoading: false, isUnlimited: true};
    }

    return {
      label: compactBytes(available),
      isLoading: false,
      isUnlimited: false,
    };
  }

  if (guestQuery.isLoading && isEmail(normalizedGuestEmail)) {
    return {label: '…', isLoading: true, isUnlimited: false};
  }

  const maxBytes = guestQuery.data?.maxBytes ?? GUEST_DEFAULT_MAX_BYTES;

  return {
    label: compactBytes(maxBytes),
    isLoading: false,
    isUnlimited: false,
  };
}
